import { ensureGeneratedSettingsForPackage, getGeneratedPackageSettings } from '@ms-cloudpack/config';
import { getDependencies } from '@ms-cloudpack/package-utilities';
import { getConsumedDependencies } from '@ms-cloudpack/api-server';
import type { PackageDefinitionsCache, GeneratedPackageSettings, Session } from '@ms-cloudpack/common-types';
import type { GeneratedPackageChanges } from './types/GeneratedPackageChanges.js';
import { neverExclude } from './neverExclude.js';
import type { InitPackageResult } from './types/InitPackageResult.js';

/**
 * Optimizes the dependencies of a package to determine if there are any missing or unused.
 * If so, adds the the missing dependencies to included and the unused dependencies to excluded.
 *
 * Returns the list of excluded dependencies added to the generated config.
 */
export async function optimizeDependencies(
  packageResults: Record<string, InitPackageResult>,
  context: {
    packages: PackageDefinitionsCache;
    session: Session;
  },
): Promise<GeneratedPackageChanges[]> {
  const { packages, session } = context;
  const { config } = session;
  const dependenciesChanges: GeneratedPackageChanges[] = [];

  // Iterate through each package
  for (const [packagePath, result] of Object.entries(packageResults)) {
    // Skip packages with build errors or no info
    if (!result?.info || result.errors?.length) {
      continue;
    }

    const definition = await packages.get(packagePath);

    // We must use the name and version of the transformed definition,
    // as that is the one that will be used in the generated config.
    const { name, version } = definition;
    // We need the non-transformed definition to catch inclusions/exclusions on retries.
    const originalDefinition = await packages.get(packagePath, { disableTransforms: true });

    const displayName = `${name}@${version}`;
    const originalDependencies = getDependencies(originalDefinition);
    const consumedDependencies = getConsumedDependencies(result.info);
    let generatedPackageSettings: GeneratedPackageSettings | undefined;

    // Iterate through dependencies and find unused.
    for (const dependencyName of Object.keys(originalDependencies)) {
      if (dependencyName.startsWith('@types/') || neverExclude(dependencyName, config)) {
        // @types dependencies are never relevant to cloudpack (and getTypesTransform removes them
        // from consideration in other parts of the code), so adding them to the generated config
        // excludedDependencies would just add bloat.
        // Or of this dependency is never excluded, so we don't need to do anything.
        continue;
      }

      generatedPackageSettings ||= getGeneratedPackageSettings({
        generatedPackageSettings: (config.generated.packageSettings ??= []),
        name,
        version,
      });

      // If this dependency is consumed, make sure it's not excluded and continue.
      if (consumedDependencies.has(dependencyName)) {
        if (generatedPackageSettings?.excludedDependencies?.includes(dependencyName)) {
          const indexOfDependency = generatedPackageSettings.excludedDependencies.indexOf(dependencyName);
          generatedPackageSettings.excludedDependencies.splice(indexOfDependency, 1);

          // Report that we removed a dependency from excluded as it is now imported by the package.
          dependenciesChanges.push({
            name,
            version,
            path: packagePath,
            changes: [
              {
                type: 'removed-excluded-dependency-optional',
                change: dependencyName,
                reason: `Imported by "${displayName}"`,
              },
            ],
          });
        }
        continue;
      }

      // The dep is not consumed. If this dependency is already excluded, continue.
      if (generatedPackageSettings?.excludedDependencies?.includes(dependencyName)) {
        continue;
      }

      // Ensure there is a generated settings entry.
      generatedPackageSettings = await ensureGeneratedSettingsForPackage({ packagePath }, { config, packages });

      // Ensure there are excluded dependencies.
      generatedPackageSettings.excludedDependencies ??= [];
      generatedPackageSettings.excludedDependencies.push(dependencyName);

      dependenciesChanges.push({
        name,
        version,
        path: packagePath,
        changes: [
          {
            type: 'excluded-dependency',
            change: dependencyName,
            reason: `Not imported by "${displayName}"`,
          },
        ],
      });
    }
  }

  return dependenciesChanges;
}
